import {
  CopyOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Collapse, Dropdown, Image, Skeleton } from "antd";
import { memo, useEffect, useMemo } from "react";
import {
  AiFillEdit,
  AiOutlineBell,
  AiOutlinePushpin,
  AiOutlineUserAdd,
} from "react-icons/ai";
import apiFactory from "../../../api";
import {
  default as imgErr,
  default as noImage,
} from "../../../assets/no-image.avif";
import AvatarComponent from "../../../components/avatar/AvatarComponent";
import { PreviewFile } from "../../../components/chat/PreviewFile";
import { PreviewVideoContent } from "../../../components/chat/PreviewVideoContent";
import { useImageLoader } from "../../../components/customHook/ImageLoader";
import {
  conversationType,
  MESSAGE_STATUS,
  PANEL_STATUS,
  role,
} from "../../../config/Constant";
import { useChatContext } from "../../../context/ChatContext";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { handleCopy } from "../../../utils/Utils";
import { ChatPreviewLink } from "./ChatPreviewLink";
import { useGlobalDownloader } from '../../../context/global/DownloadProvider';
import "./style.scss";

const { Panel } = Collapse;

const PanelView = ({ jumpToMessage, openModal, resourceViewer }) => {
  const { selectedConversation, setPanelStatus } = useMenuContext();
  const { resources, setResources, setViewMode, setArchiveType } =
    useChatContext();
  const { languageMap } = useInfoUser();

  const getResourcesPanel = async () => {
    if (!selectedConversation?.conversationId) return;
    try {
      const result = await apiFactory.resourceApi.getResourcesPanel(
        selectedConversation?.conversationId,
      );
      setResources(result);
    } catch (err) {
      console.error("Error loading resources:", err);
    }
  };

  useEffect(() => {
    getResourcesPanel();
  }, [selectedConversation?.conversationId]);

  return (
    <div className="mb-[60px]">
      <div className="p-4 border-b-[8px] border-[#eaeef0] flex flex-col items-center">
        <div className="flex flex-col items-center">
          <AvatarComponent conversation={selectedConversation} />
          <div className="flex items-center mt-2 gap-2 ">
            <span className="text-center font-semibold">
              {selectedConversation?.name}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex justify-around w-full">
          <ActionCircle
            icon={<AiOutlineBell size={20} />}
            text={
              languageMap?.["select.conversation.option.turnOnNotifications"] ??
              "Turn on notifications"
            }
            disabled
          />
          <ActionCircle
            icon={<AiOutlinePushpin size={20} />}
            text={
              languageMap?.["select.conversation.option.pinnedConversation"] ??
              "Pin conversation"
            }
            disabled
          />
          <ActionCircle
            icon={<AiOutlineUserAdd size={20} />}
            text={
              languageMap?.["select.conversation.option.addMember"] ??
              "Add members"
            }
            disabled
          />
          {selectedConversation?.type !== conversationType.SYSTEM &&
            selectedConversation?.conversationStatus === "ACTIVE" &&
            (selectedConversation?.role === role?.HOST ||
              selectedConversation?.type === conversationType.PERSONAL) && (
              <button onClick={openModal}>
                <div className="icon-edit">
                  <ActionCircle
                    icon={<AiFillEdit onClick={openModal} size={20} />}
                    text="Edit group"
                  />
                </div>
              </button>
            )}
        </div>
      </div>

      {/* Collapse sections */}
      <Collapse
        ghost
        defaultActiveKey={["3", "4", "5"]}
        expandIconPosition="end"
      >
        {/* Ảnh/Video */}
        <Panel
          className="custom-panel"
          header={languageMap?.["modal.tabHeader.Images"] ?? "Images"}
          key="3"
        >
          <div className="grid grid-cols-3 gap-2">
            {resources?.images?.length === 0 ? (
              <p className="text-sm text-gray-500">No photos yet</p>
            ) : (
              resources?.images?.map((img, i) => (
                <ImageItem
                  key={img?.resourceId || i}
                  idx={img?.resourceId}
                  file={img}
                  jumpToMessage={jumpToMessage}
                  openViewer={() =>
                    resourceViewer?.handleOpenViewer(
                      {
                        ...img?.media,
                        createdAt: img?.createdAt,
                        userId: img?.senderId,
                      },
                      img?.createdAt,
                    )
                  }
                />
              ))
            )}
          </div>

          {resources?.images?.length > 0 && (
            <Button
              className="w-full mt-2"
              onClick={() => {
                setArchiveType("IMAGE");
                setPanelStatus(PANEL_STATUS.MEDIA_STORAGE);
              }}
            >
              {languageMap?.["modal.message.showMore"] ?? "show more"}
            </Button>
          )}
        </Panel>

        {/* File */}
        <Panel
          className="custom-panel"
          header={languageMap?.["modal.tabHeader.fileVideo"] ?? "File/Video"}
          key="4"
        >
          {resources?.files?.length === 0 ? (
            <p className="text-sm text-gray-500">No files yet</p>
          ) : (
            <>
              {resources?.files?.map((file, idx) => (
                <FileItem
                  key={file?.requestUuid || idx}
                  idx={file?.requestUuid}
                  file={file}
                  jumpToMessage={jumpToMessage}
                />
              ))}
              {resources?.files?.length > 0 && (
                <Button
                  className="w-full mt-2"
                  onClick={() => {
                    setArchiveType("FILE");
                    setPanelStatus(PANEL_STATUS.MEDIA_STORAGE);
                  }}
                >
                  {languageMap?.["modal.message.showMore"] ?? "show more"}
                </Button>
              )}
            </>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};

function ActionCircle({ icon, text, disabled, onClick }) {
  return (
    <div className="flex flex-col items-center text-xs">
      <Button
        shape="circle"
        icon={icon}
        size="large"
        disabled={disabled}
        onClick={onClick}
      />
      <span
        className={`mt-1 text-center w-16 ${disabled ? "text-gray-500" : "text-gray-600"}`}
      >
        {text}
      </span>
    </div>
  );
}

export const ImageItem = memo(
  ({ file, idx, height = 80, width = "100%", jumpToMessage, openViewer }) => {
    const {
      imageUrl,
      loading,
      error: imageError,
    } = useImageLoader(
      file?.media?.previewImgResourceId ||
        file?.previewImgResourceId ||
        file?.media?.resourceId ||
        file?.resourceId,
    );
    const downloader = useGlobalDownloader();

    const renderContent = useMemo(() => {
      const imageClassName = "cursor-pointer w-full h-full object-cover";

      if (imageError) {
        return (
          <Image
            src={imgErr}
            onClick={openViewer}
            preview={false}
            loading="lazy"
            className={imageClassName}
          />
        );
      }

      if (imageUrl) {
        return (
          <Image
            preview={false}
            height={height}
            width={width}
            src={imageUrl}
            fallback={noImage}
            onClick={openViewer}
            loading="lazy"
            className="rounded cursor-pointer object-cover border border-gray-300"
          />
        );
      }

      if (loading) {
        return <Skeleton.Image active className="w-full" style={{ height }} />;
      }

      return <div key={idx} className="w-full h-full"></div>;
    }, [imageUrl, loading, imageError]);

    return (
      <div
        className="relative w-full object-cover parent-action-menu"
        key={idx}
      >
        {renderContent}

        <ActionMenu
          file={file}
          downloader={downloader}
          actions={["download"]}
          jumpToMessage={jumpToMessage}
        />
      </div>
    );
  },
);

export const FileItem = memo(({ file, idx, jumpToMessage }) => {
  const downloader = useGlobalDownloader();
  return (
    <div className="relative " key={idx}>
      {file?.media?.videoImgResourceId ? (
        <PreviewVideoContent
          file={{
            ...file?.media,
            createdAt: file?.createdAt,
            userId: file?.senderId,
          }}
          isSelf={true}
          isInPanel={true}
        />
      ) : (
        <PreviewFile
          requestUuid={file?.requestId}
          content={null}
          file={file?.media}
          contentType={MESSAGE_STATUS.FILE}
          percentProgress={0}
          totalVolume={file?.media?.volume}
          path="conversation"
          isPinned={false}
          isInPanel={true}
        />
      )}

      <ActionMenu
        file={file}
        downloader={downloader}
        jumpToMessage={jumpToMessage}
      />
    </div>
  );
});

export const LinkItem = memo(({ link, idx, jumpToMessage }) => {
  const downloader = useGlobalDownloader();

  return (
    <div className="relative parent-action-menu" key={idx}>
      <ChatPreviewLink previewLink={link?.linkPreview} isInPanel={true} />

      <ActionMenu
        file={link}
        downloader={downloader}
        actions={["copy"]}
        jumpToMessage={jumpToMessage}
      />
    </div>
  );
});

export const ActionMenu = memo(
  ({ file, downloader, actions = [], jumpToMessage }) => {
    const { languageMap } = useInfoUser();
    const { startDownload } = downloader;

    const onMenuClick = (keyDom) => {
      const { key } = keyDom;

      switch (key) {
        case "share":
          console.log("Chia sẻ");
          break;

        case "save":
          console.log("Lưu Cloud");
          break;

        case "origin":
          if (file?.messageId) {
            jumpToMessage({
              messageId: file?.messageId,
              requestUuid: file?.requestUuid,
              createdAt: file?.createdAt,
            });
          }
          break;

        case "copy":
          handleCopy(file?.linkPreview?.url || "");
          break;

        case "select":
          console.log("Chọn nhiều");
          break;

        case "download":
          if (file?.media?.resourceId) {
            startDownload(
              file?.media?.resourceId,
              file?.originalName || file?.fileName || file?.media?.originalName,
            );
          }
          break;
        default:
          console.log("Không có action");
      }
    };

    const defaultItems = [
      {
        key: "origin",
        label: languageMap?.["conversation.message.jump"] ?? "Jump to message",
        icon: <FileSearchOutlined />,
      },
    ];

    const extraItems = actions
      ?.map((act) => {
        switch (act) {
          case "copy":
            return { key: "copy", label: "copy", icon: <CopyOutlined /> };
          case "download":
            return {
              key: "download",
              label:
                languageMap?.["conversation.message.media.download"] ??
                "Download",
              icon: <DownloadOutlined />,
            };
          default:
            return null;
        }
      })
      .filter(Boolean);

    return (
      <div className="absolute top-1 right-1 children-action-menu">
        <Dropdown
          menu={{
            items: [...defaultItems, ...extraItems],
            onClick: onMenuClick,
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            className="!bg-white hover:!bg-white active:!bg-white !shadow rounded-full border border-gray"
          />
        </Dropdown>
      </div>
    );
  },
);

export { PanelView };

